import { type FC, type ReactNode } from "react";
import { alpha, Avatar, Paper, Stack, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import { initials } from "./model/people";
import { threadTexts } from "./texts";

/** Кто говорит: зритель, команда, внутренняя заметка команды, помощник. */
export type MessageTone = "viewer" | "team" | "note" | "assistant";

interface Props {
  tone: MessageTone;
  name: string;
  /** Имя для букв аватара; у помощника не нужно. */
  avatarName?: string | null;
  time: string;
  /** Подписи и значки после имени (канал, «через помощника»). */
  meta?: ReactNode;
  /** Действия справа от времени (исходное письмо). */
  actions?: ReactNode;
  children: ReactNode;
  dataAttributes?: Record<string, string>;
}

const bubbleSx = (tone: MessageTone, end: boolean): SxProps<Theme> => ({
  px: 2,
  py: 1.25,
  borderRadius: 2,
  // Угол к аватару острее: видно, чья реплика.
  ...(end ? { borderTopRightRadius: 2 } : { borderTopLeftRadius: 2 }),
  ...(tone === "team" && {
    bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.12),
  }),
  ...(tone === "assistant" && { bgcolor: "action.hover" }),
  ...(tone === "note" && {
    bgcolor: (theme: Theme) => alpha(theme.palette.warning.main, 0.1),
    borderColor: (theme: Theme) => alpha(theme.palette.warning.main, 0.45),
  }),
});

const MessageAvatar: FC<{ tone: MessageTone; name?: string | null }> = ({
  tone,
  name,
}) => {
  const letters = initials(name);
  const sx: SxProps<Theme> = { width: 36, height: 36, fontSize: 15 };
  if (tone === "assistant") {
    return (
      <Avatar sx={{ ...sx, bgcolor: "secondary.main" }}>
        <SmartToyOutlinedIcon fontSize="small" />
      </Avatar>
    );
  }
  if (tone === "viewer") {
    return (
      <Avatar sx={sx}>
        {letters || <PersonOutlineIcon fontSize="small" />}
      </Avatar>
    );
  }
  return (
    <Avatar
      sx={{
        ...sx,
        bgcolor: tone === "note" ? "warning.main" : "primary.main",
        color:
          tone === "note" ? "warning.contrastText" : "primary.contrastText",
      }}
    >
      {letters || <PersonOutlineIcon fontSize="small" />}
    </Avatar>
  );
};

/**
 * Реплика ленты: аватар, строка имени со временем и каналом, пузырь.
 * Зритель и его вопросы слева, команда и помощник справа; заметка команды
 * подкрашена цветом предупреждения и подписана замком.
 */
export const MessageRow: FC<Props> = ({
  tone,
  name,
  avatarName,
  time,
  meta,
  actions,
  children,
  dataAttributes,
}) => {
  const end = tone !== "viewer";
  return (
    <Stack
      direction={end ? "row-reverse" : "row"}
      spacing={1.5}
      alignItems="flex-start"
      {...dataAttributes}
    >
      <MessageAvatar tone={tone} name={avatarName ?? name} />
      <Stack
        spacing={0.5}
        alignItems={end ? "flex-end" : "flex-start"}
        sx={{ minWidth: 0, maxWidth: { xs: "calc(100% - 48px)", md: "78%" } }}
      >
        <Stack
          direction={end ? "row-reverse" : "row"}
          spacing={1}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          sx={{ minHeight: 28 }}
        >
          <Typography variant="subtitle2" component="span">
            {name}
          </Typography>
          {meta}
          <Typography variant="caption" color="text.secondary">
            {time}
          </Typography>
          {actions}
        </Stack>
        <Paper
          variant={
            tone === "viewer" || tone === "note" ? "outlined" : "elevation"
          }
          sx={bubbleSx(tone, end)}
        >
          {tone === "note" && (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ color: "warning.main", mb: 0.5 }}
            >
              <LockOutlinedIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" fontWeight={600}>
                {threadTexts.internalNote()}
              </Typography>
            </Stack>
          )}
          {children}
        </Paper>
      </Stack>
    </Stack>
  );
};
